import { Component, Inject } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MD_DIALOG_DATA, MdDialogRef, MdSnackBar } from "@angular/material";
import { Router } from "@angular/router";
import Swal from "sweetalert2";
import { LicenseService } from "../../../../services/LicenseService";
import { AdalService } from "../../../../shared/adal.service";
import { AppLoaderService } from "../../../../../services/app-loader/app-loader.service";

@Component({
  selector: "generate-gis360-license",
  styles: [require('./generate-gis360-license.component.css')],
  template: require('./generate-gis360-license.component.html'),
})
export class GenerateGIS360LicenseComponent {
  dialogTitle = "";
  errorMessage = "";
  submitted = false;
  gis360LicenseForm: FormGroup;
  // Set when the popup is opened from Edit; null for Generate
  selectedLicense = null;

  constructor(
    private dialogRef: MdDialogRef<GenerateGIS360LicenseComponent>,
    private formBuilder: FormBuilder,
    private licenseService: LicenseService,
    private appLoader: AppLoaderService,
    private snackBar: MdSnackBar,
    private adalService: AdalService,
    private router: Router,
    @Inject(MD_DIALOG_DATA) public dialogData: any,
  ) {
    if (!this.adalService.getCachedToken()) {
      this.dialogRef.close(null);
      this.router.navigate(["login"]);
    }
    this.dialogTitle = this.dialogData.dialogTitle;
    this.selectedLicense = this.dialogData.SelectedLicense || null;
    this.buildForm();
    if (this.selectedLicense) {
      this.gis360LicenseForm.patchValue({
        SalesOrderNo: this.selectedLicense.SalesOrderNo,
        CustomerName: this.selectedLicense.CustomerName,
        NoOfLicenses: this.selectedLicense.NoOfLicenses,
        // API dates are "yyyy-MM-ddTHH:mm:ss"; the date input needs "yyyy-MM-dd"
        ValidFrom: String(this.selectedLicense.ValidFrom).substring(0, 10),
        ExpiryDate: String(this.selectedLicense.InitialExpiryDate).substring(0, 10),
      });
    }
  }

  buildForm() {
    this.gis360LicenseForm = this.formBuilder.group(
      {
        SalesOrderNo: ["", [Validators.required, Validators.maxLength(50)]],
        CustomerName: ["", [Validators.required, Validators.maxLength(50)]],
        NoOfLicenses: [1, [Validators.required, Validators.min(1)]],
        ValidFrom: [this.toDateInputValue(new Date()), Validators.required],
        ExpiryDate: ["", Validators.required],
      },
      { validator: this.expiryAfterValidFrom },
    );
  }

  // Expiry Date must be after Valid From (dates are yyyy-MM-dd strings, so they compare correctly as text)
  expiryAfterValidFrom(group: FormGroup) {
    const validFrom = group.get("ValidFrom").value;
    const expiryDate = group.get("ExpiryDate").value;
    if (validFrom && expiryDate && expiryDate <= validFrom) {
      return { expiryBeforeValidFrom: true };
    }
    return null;
  }

  toDateInputValue(date: Date): string {
    const month = ("0" + (date.getMonth() + 1)).slice(-2);
    const day = ("0" + date.getDate()).slice(-2);
    return date.getFullYear() + "-" + month + "-" + day;
  }

  onSave() {
    this.submitted = true;
    if (this.gis360LicenseForm.invalid) {
      return;
    }
    // Edit with fewer licenses removes the newest License Keys, so confirm first
    const noOfKeysToRemove = this.selectedLicense
      ? this.selectedLicense.NoOfLicenses - Number(this.gis360LicenseForm.value.NoOfLicenses)
      : 0;
    if (noOfKeysToRemove > 0) {
      Swal.fire({
        icon: "warning",
        title: noOfKeysToRemove + " license key(s) will be removed",
        text: "The newest license key(s) of this sales order will stop working. Do you want to continue?",
        showCancelButton: true,
        showCloseButton: true,
        showConfirmButton: true,
        confirmButtonText: "Yes",
        confirmButtonColor: "#FFCA28",
        cancelButtonText: "No",
      }).then((result) => {
        if (result.isConfirmed) {
          this.checkSalesOrderNoAndSave();
        }
      });
    } else {
      this.checkSalesOrderNoAndSave();
    }
  }

  checkSalesOrderNoAndSave() {
    if (this.checkSalesOrderNoExists()) {
      Swal.fire({
        title: "This sales order no already exists",
        text: "Are you sure you want to use this Sales Order No?",
        showCancelButton: true,
        showCloseButton: true,
        showConfirmButton: true,
        confirmButtonText: "Yes",
        confirmButtonColor: "#FFCA28",
        cancelButtonText: "No",
      }).then((result) => {
        if (result.isConfirmed) {
          this.generateLicense();
        } else {
          this.gis360LicenseForm.patchValue({
            SalesOrderNo: "",
          });
        }
      });
    } else {
      this.generateLicense();
    }
  }

  // Same check as the other products: compare with the license list already loaded on the GIS360 page
  // (on Edit, the license being edited is skipped so an unchanged Sales Order No does not warn)
  checkSalesOrderNoExists(): boolean {
    const salesOrderNo = this.gis360LicenseForm.value.SalesOrderNo.trim().toUpperCase();
    const licenseList = this.dialogData.GIS360LicenseData || [];
    return licenseList.some((element) => {
      if (this.selectedLicense && element.LicenseMasterId == this.selectedLicense.LicenseMasterId) {
        return false;
      }
      if (element.SalesOrderNo != null) {
        return salesOrderNo == element.SalesOrderNo.trim().toUpperCase();
      } else {
        return false;
      }
    });
  }

  generateLicense() {
    const formValue = this.gis360LicenseForm.getRawValue();
    // GIS360 keeps only the sales order no and customer name; all other billing/shipping fields stay null
    const billingShippingDetail = {
      Number: formValue.SalesOrderNo.trim(), // addBillingShippingDetails reads the sales order no from "Number"
      BillToName: formValue.CustomerName.trim(),
    };

    this.appLoader.open();
    this.licenseService.addBillingShippingDetail(billingShippingDetail).subscribe(
      (data) => {
        if (data.IsError || data.DataObject == null) {
          this.appLoader.close();
          this.errorMessage = data.Error || "Unable to save customer details.";
          return;
        }
        const userId = Number(localStorage.getItem("userid"));
        const payload: any = {
          SalesOrderNo: formValue.SalesOrderNo.trim(),
          CustomerId: data.DataObject.CustomerId,
          ShippingAddressId: data.DataObject.ShippingAddressId,
          NoOfLicenses: Number(formValue.NoOfLicenses),
          ValidFrom: formValue.ValidFrom,
          ExpiryDate: formValue.ExpiryDate,
        };
        if (this.selectedLicense) {
          payload.LicenseMasterId = this.selectedLicense.LicenseMasterId;
          payload.UpdatedBy = userId;
        } else {
          payload.CreatedBy = userId;
        }
        const request = this.selectedLicense
          ? this.licenseService.UpdateGIS360License(payload)
          : this.licenseService.GenerateGIS360License(payload);
        request.subscribe(
          (res) => {
            this.appLoader.close();
            if (!res.IsError && res.DataObject != null) {
              this.errorMessage = "";
              this.showGeneratedKeys(res.DataObject);
            } else {
              this.errorMessage = res.Error;
            }
          },
          (err) => {
            this.appLoader.close();
            this.errorMessage = "Something went wrong.";
          },
        );
      },
      (err) => {
        this.appLoader.close();
        this.errorMessage = "Something went wrong.";
      },
    );
  }

  showGeneratedKeys(result) {
    const keys: string[] = result.LicenseKeys || [];
    const successMessage = this.selectedLicense ? "License updated successfully" : "License generated successfully";
    // Edit without new License Keys: nothing to show, just confirm
    if (keys.length == 0) {
      this.snackBar.open(successMessage, "", { duration: 3000 });
      this.dialogRef.close(result);
      return;
    }
    const keyList = keys.map((key) => "<div style='font-family: monospace; font-size: 16px'>" + this.escapeHtml(key) + "</div>").join("");
    Swal.fire({
      icon: "success",
      title: keys.length + (this.selectedLicense ? " new license key(s) added" : " license key(s) generated"),
      html: "<div>Sales Order No: <b>" + this.escapeHtml(result.SalesOrderNo) + "</b></div><br/>" + keyList,
      confirmButtonText: "Ok",
      confirmButtonColor: "#FFCA28",
    }).then(() => {
      this.snackBar.open(successMessage, "", { duration: 3000 });
      this.dialogRef.close(result);
    });
  }

  escapeHtml(value: string): string {
    const div = document.createElement("div");
    div.textContent = value == null ? "" : String(value);
    return div.innerHTML;
  }

  onCancel() {
    this.dialogRef.close(false);
  }

  hasError(controlName: string): boolean {
    const control = this.gis360LicenseForm.get(controlName);
    return control.invalid && (control.touched || this.submitted);
  }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/app/license-portal/gis360/popup/generate-gis360-license/generate-gis360-license.component.ts